import type { FilterOption, FilterOptionConfig } from '@brightdrop/bd-ui';
import type { ResolvedUnits } from '@brightdrop/localization-core';
import { TranslatedLabel, translateText } from '@gm-commercial/translations';
import type { BDFilterModalOptionConfig } from '~/common/components/BDFilter/BDFilterModal';
import { AssetServiceStatus } from '~/common/models/asset.model';
import {
  BatteryChargingStatus,
  BatterySocStatus,
  BrakePadStatus,
  EstimatedRangeStatus,
  FuelStatus,
  LastCommunicatedStatus,
  OilLifeStatus,
  ReadyStatus,
  ReportDiagnosticState,
  SafetyCompliance,
} from '~/common/models/asset-report.model';
import { ConnectivityStatusType } from '~/common/models/common.model';
import {
  ApiToFieldActionTypeMap,
  FieldActionType,
} from '~/common/models/recalls.model';
import type { AssetListFilterCriteria } from '../../assetsSlice';
import type { ASSET_LIST_REPORT_FILTER_MESSAGES } from '../AssetListReportViewFilterModal.messages';
import {
  BRAKE_PAD_FILTER_MODAL_STATUS,
  ESTIMATED_RANGE_MODAL_STATUS,
  FUEL_CHARGE_FILTER_MODAL_STATUS,
  OIL_LIFE_FILTER_MODAL_STATUS,
} from '../AssetReportView.constant';
import {
  getDiagnosticModalLabel,
  getDiagnosticsFilterModalLabel,
  getEstimatedRangeFilterModalLabel,
} from '../utils/report.utils';

const diagnosticsHealthFilter = () =>
  ({
    title: translateText('asset:diagnostics'),
    id: 'diagnostics-health',
    type: 'diagnosticsHealthStatus',
    options: getDiagnosticsHealthConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;
const availabilityFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['asset:vehicle.details.availability'],
    id: 'availability',
    type: 'availabilityStatus',
    options: getAvailabilityConfig(valueLabels),
  }) as FilterOptionConfig<AssetServiceStatus, AssetListFilterCriteria>;
const washerFluidFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['asset:tableColumnLabels.washerFluidStatus'],
    id: 'washer-fluid-status',
    type: 'washerFluidStatus',
    options: getAssetFluidConfig(valueLabels),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;
const batterySocFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels
) =>
  ({
    title: filterLabels['asset:tableColumnLabels.batterySocStatus'],
    id: 'battery-soc-status',
    type: 'batterySocStatus',
    options: getBatterySocConfig(),
  }) as FilterOptionConfig<BatterySocStatus, AssetListFilterCriteria>;

const batteryChargingFilter = (options: BatteryChargingStatus[]) =>
  ({
    title: translateText('asset:tableColumnLabels.batteryChargingStatus'),
    id: 'battery-charging-status',
    type: 'batteryChargingStatus',
    options: getChargingConfig(options),
  }) as FilterOptionConfig<BatteryChargingStatus, AssetListFilterCriteria>;

const brakeFluidFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['asset:tableColumnLabels.brakeFluidStatus'],
    id: 'brake-fluid-status',
    type: 'brakeFluidStatus',
    options: getAssetFluidConfig(valueLabels),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const tireStatusFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['asset:tableColumnLabels.tireStatus'],
    id: 'tire-status',
    type: 'tireStatus',
    options: getTireConfig(valueLabels),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const connectivityStatusFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['common:lastCommunicated'],
    id: 'lastCommunicated-status',
    type: 'lastCommunicatedStatus',
    options: getConnectivityConfig(valueLabels),
  }) as FilterOptionConfig<LastCommunicatedStatus, AssetListFilterCriteria>;

const fuelFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels
) =>
  ({
    title: filterLabels['asset:fuel'],
    id: 'fuel-status',
    type: 'fuelStatus',
    options: getFuelConfig(),
  }) as FilterOptionConfig<FuelStatus, AssetListFilterCriteria>;

const estimatedRangeFilter = (units: ResolvedUnits, locale: string) =>
  ({
    title: translateText('asset:detail.rangeCard.estimatedRange'),
    id: 'estimated-range-status',
    type: 'estimatedRangeStatus',
    options: getEstimatedRangeConfig(units, locale),
  }) as FilterOptionConfig<EstimatedRangeStatus, AssetListFilterCriteria>;

const dieselExhaustFluidFilter = () =>
  ({
    title: translateText('asset:diagnosticMapping.diesel_exhaust'),
    id: 'diesel-exhaust-fluid-status',
    type: 'dieselExhaustFluidStatus',
    options: getDieselExhaustConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const getCommonFilters = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  const filters: BDFilterModalOptionConfig<AssetListFilterCriteria>[] = [];
  filters.push(connectivityStatusFilter(filterLabels, valueLabels));
  return filters;
};

export const AVAILABLE_OIL_LIFE_OPTIONS = [
  OilLifeStatus.GOOD,
  OilLifeStatus.IMPAIRED,
  OilLifeStatus.CRITICAL,
];
export const getOilLifeConfig = (): FilterOption<OilLifeStatus>[] =>
  AVAILABLE_OIL_LIFE_OPTIONS.map((oilLifeStatus) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        OIL_LIFE_FILTER_MODAL_STATUS,
        oilLifeStatus
      ),
      value: oilLifeStatus,
    };
  });
const oilLifeFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels
) =>
  ({
    title: filterLabels['asset:oilLife'],
    id: 'oilLife-status',
    type: 'oilLifeStatus',
    options: getOilLifeConfig(),
  }) as FilterOptionConfig<OilLifeStatus, AssetListFilterCriteria>;

export const AVAILABLE_BRAKE_PAD_OPTIONS = [
  BrakePadStatus.GOOD,
  BrakePadStatus.IMPAIRED,
  BrakePadStatus.CRITICAL,
];
export const getBrakePadConfig = (): FilterOption<BrakePadStatus>[] =>
  AVAILABLE_BRAKE_PAD_OPTIONS.map((BrakePadStatus) => {
    return {
      label: getDiagnosticModalLabel(
        BRAKE_PAD_FILTER_MODAL_STATUS,
        BrakePadStatus
      ),
      value: BrakePadStatus,
    };
  });
const brakePadFrontFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels
) =>
  ({
    title: filterLabels['asset:brakePadFront'],
    id: 'brakePadFront-status',
    type: 'brakePadFrontStatus',
    options: getBrakePadConfig(),
  }) as FilterOptionConfig<BrakePadStatus, AssetListFilterCriteria>;

const brakePadRearFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels
) =>
  ({
    title: filterLabels['asset:brakePadRear'],
    id: 'brakePadRear-status',
    type: 'brakePadRearStatus',
    options: getBrakePadConfig(),
  }) as FilterOptionConfig<BrakePadStatus, AssetListFilterCriteria>;

const AVAILABLE_ENGINE_AIR_FILTER_OPTIONS = [
  ReportDiagnosticState.GOOD,
  ReportDiagnosticState.IMPAIRED,
  ReportDiagnosticState.CRITICAL,
];
const getEngineAirFilterConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_ENGINE_AIR_FILTER_OPTIONS.map((option) => {
    const translationKey =
      `asset:filterModalLabels.engineAirFilterStatus.${option}` as keyof typeof valueLabels;
    return {
      label: translateText(translationKey as string),
      value: option,
    };
  });
const engineAirFilter = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
) =>
  ({
    title: filterLabels['asset:vehicle.diagnostic.engineAirFilter'],
    id: 'oengineAirFilter-status',
    type: 'engineAirFilterStatus',
    options: getEngineAirFilterConfig(valueLabels),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

export const getConnectivityBoardingStatusConfig = (
  isNotEquippedFeatureFlag: boolean
): FilterOption<ConnectivityStatusType>[] => {
  const availableOptions: ConnectivityStatusType[] = [
    isNotEquippedFeatureFlag
      ? ConnectivityStatusType.NOT_EQUIPPED
      : ConnectivityStatusType.VERIFIED,
    ConnectivityStatusType.CONNECTION_PENDING,
    ConnectivityStatusType.CONNECTED,
  ];

  return availableOptions.map((status) => {
    return {
      label: translateText(
        `asset:filterModalLabels.connectivityBoardingStatus.${status}`
      ),
      value: status,
    };
  });
};

const connectivityBoardingStatusFilter = (isNotEquippedFeatureFlag: boolean) =>
  ({
    title: translateText('common:status'),
    id: 'connectivity-boarding-status',
    type: 'connectivityBoardingStatus',
    options: getConnectivityBoardingStatusConfig(isNotEquippedFeatureFlag),
  }) as FilterOptionConfig<ConnectivityStatusType, AssetListFilterCriteria>;

const fieldActionTypeFilter = () =>
  ({
    title: (
      <TranslatedLabel
        translationKey={'asset:recalls.recallsFieldActionTypes'}
        typographyVariant={'body_medium'}
      />
    ),
    id: 'fieldAction',
    type: 'fieldActionTypeStatus',
    options: getFieldActionConfig(),
  }) as FilterOptionConfig<FieldActionType, AssetListFilterCriteria>;

export const getReadinessFilterModalConfig = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels,
  hideAvailabilityFilter = false,
  isNotEquippedFeatureFlag: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  return [
    connectivityBoardingStatusFilter(isNotEquippedFeatureFlag),
    diagnosticsHealthFilter(),
    ...getCommonFilters(filterLabels, valueLabels),
    ...(!hideAvailabilityFilter
      ? [availabilityFilter(filterLabels, valueLabels)]
      : []),
    fuelFilter(filterLabels),
    batterySocFilter(filterLabels),
    tireStatusFilter(filterLabels, valueLabels),
    engineAirFilter(filterLabels, valueLabels),
    washerFluidFilter(filterLabels, valueLabels),
    brakeFluidFilter(filterLabels, valueLabels),
    brakePadFrontFilter(filterLabels),
    brakePadRearFilter(filterLabels),
    oilLifeFilter(filterLabels),
    dieselExhaustFluidFilter(),
  ];
};

export const getFluidsFilterModalConfig = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels,
  _hideAvailabilityFilter: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  return [
    ...getCommonFilters(filterLabels, valueLabels),
    oilLifeFilter(filterLabels),
    fuelFilter(filterLabels),
    brakeFluidFilter(filterLabels, valueLabels),
    washerFluidFilter(filterLabels, valueLabels),
    dieselExhaustFluidFilter(),
  ];
};

export const getBatteryFilterModalConfig = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  _hideAvailabilityFilter: boolean,
  units: ResolvedUnits,
  locale: string,
  isNotEquippedFeatureFlag: boolean,
  isVehicleListEVsConnectedOnlyEnabled = false
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  const typeFilter: BDFilterModalOptionConfig<AssetListFilterCriteria>[] = [];
  return typeFilter.concat([
    ...(isVehicleListEVsConnectedOnlyEnabled
      ? []
      : [connectivityBoardingStatusFilter(isNotEquippedFeatureFlag)]),
    batterySocFilter(filterLabels),
    batteryChargingFilter(BATTERY_READINESS_CHARGING_FILTER_OPTIONS),
    estimatedRangeFilter(units, locale),
  ]);
};

export const getTiresFilterModalConfig = (
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  return [
    ...getCommonFilters(filterLabels, valueLabels),
    tireStatusFilter(filterLabels, valueLabels),
  ];
};
export const getRecallsFilterModalConfig =
  (): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
    return [fieldActionTypeFilter()];
  };
export const getAssetReadyConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<ReadyStatus>[] =>
  [ReadyStatus.READY, ReadyStatus.NOT_READY].map((status) => {
    return {
      label:
        valueLabels[
          `asset:filterModalLabels.readiness.${status}` as keyof typeof valueLabels
        ],
      value: status,
    };
  });

export const AVAILABLE_DIAGNOSTICS_HEALTH_OPTIONS = [
  ReportDiagnosticState.CRITICAL,
  ReportDiagnosticState.IMPAIRED,
  ReportDiagnosticState.OK,
];
export const getDiagnosticsHealthConfig =
  (): FilterOption<ReportDiagnosticState>[] =>
    AVAILABLE_DIAGNOSTICS_HEALTH_OPTIONS.map((diagnosticStatus) => {
      return {
        label: translateText(
          `asset:filterModalLabels.diagnostics.${diagnosticStatus}`
        ),
        value: diagnosticStatus,
      };
    });

export const AVAILABLE_AVAILABILITY_OPTIONS = [
  AssetServiceStatus.IN_SERVICE,
  AssetServiceStatus.OUT_OF_SERVICE,
];
export const getAvailabilityConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<AssetServiceStatus>[] =>
  AVAILABLE_AVAILABILITY_OPTIONS.map((availabilityStatus) => {
    return {
      label: valueLabels[`asset:serviceStatus.${availabilityStatus}`],
      value: availabilityStatus,
    };
  });

export const getAssetFluidConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<ReportDiagnosticState>[] =>
  [ReportDiagnosticState.GOOD, ReportDiagnosticState.CRITICAL].map(
    (fluidStatus) => {
      return {
        label:
          valueLabels[
            `asset:filterModalLabels.fluidStatus.${fluidStatus}` as keyof typeof valueLabels
          ],
        value: fluidStatus,
      };
    }
  );
export const getDieselExhaustConfig =
  (): FilterOption<ReportDiagnosticState>[] =>
    [ReportDiagnosticState.GOOD, ReportDiagnosticState.CRITICAL].map(
      (status) => {
        return {
          label: translateText(
            `asset:filterModalLabels.dieselExhaustFluidDiagnosticStatus.${status}`
          ),
          value: status,
        };
      }
    );
export const AVAILABLE_BATTERY_SOC_FILTER_OPTIONS =
  Object.values(BatterySocStatus);
export const getBatterySocConfig = (): FilterOption<BatterySocStatus>[] =>
  AVAILABLE_BATTERY_SOC_FILTER_OPTIONS.map((status) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        FUEL_CHARGE_FILTER_MODAL_STATUS,
        status
      ),
      value: status,
    };
  });
export const AVAILABLE_ESTIMATED_RANGE_FILTER_OPTIONS =
  Object.values(EstimatedRangeStatus);
export const getEstimatedRangeConfig = (
  units: ResolvedUnits,
  locale: string
): FilterOption<EstimatedRangeStatus>[] =>
  AVAILABLE_ESTIMATED_RANGE_FILTER_OPTIONS.map((status) => {
    return {
      label: getEstimatedRangeFilterModalLabel(
        ESTIMATED_RANGE_MODAL_STATUS,
        status,
        'KM',
        units.distance,
        locale
      ),
      value: status,
    };
  });
export const AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS = Object.values(
  LastCommunicatedStatus
);
export const getConnectivityConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<LastCommunicatedStatus>[] =>
  AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS.map((status) => {
    return {
      label: valueLabels[`common:propertyState.${status}`],
      value: status,
    };
  });

export const FLEET_READINESS_CHARGING_FILTER_OPTIONS = [
  BatteryChargingStatus.CHARGING,
];
export const BATTERY_READINESS_CHARGING_FILTER_OPTIONS = [
  BatteryChargingStatus.CHARGING,
  BatteryChargingStatus.ABORTED,
  BatteryChargingStatus.COMPLETED,
  BatteryChargingStatus.UNPLUGGED,
  BatteryChargingStatus.IDLE,
  BatteryChargingStatus.PLUGGED,
];

export const AVAILABLE_FUEL_FILTER_OPTIONS = Object.values(FuelStatus);
export const getFuelConfig = (): FilterOption<FuelStatus>[] =>
  AVAILABLE_FUEL_FILTER_OPTIONS.map((status) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        FUEL_CHARGE_FILTER_MODAL_STATUS,
        status
      ),
      value: status,
    };
  });

export const getChargingConfig = (
  options: BatteryChargingStatus[]
): FilterOption<BatteryChargingStatus>[] =>
  options.map((chargingStatus) => {
    return {
      label: translateText(
        `asset:filterModalLabels.chargingStatus.${chargingStatus}`
      ),
      value: chargingStatus,
    };
  });

export const AVAILABLE_TIRE_FILTER_OPTIONS = [
  ReportDiagnosticState.GOOD,
  ReportDiagnosticState.CRITICAL,
  ReportDiagnosticState.IMPAIRED,
];
export const getTireConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_TIRE_FILTER_OPTIONS.map((tireStatus) => {
    return {
      label:
        valueLabels[
          `asset:filterModalLabels.tireStatus.${tireStatus}` as keyof typeof valueLabels
        ],
      value: tireStatus,
    };
  });

export const getSafetyComplianceConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<SafetyCompliance>[] =>
  [SafetyCompliance.COMPLIANT, SafetyCompliance.NON_COMPLIANT].map(
    (compliance) => {
      return {
        label:
          valueLabels[`asset:filterModalLabels.safetyCompliance.${compliance}`],
        value: compliance,
      };
    }
  );
export const AVAILABLE_FAULT_FILTER_OPTIONS = [
  ReportDiagnosticState.CRITICAL,
  ReportDiagnosticState.IMPAIRED,
];
export const getFaultConfig = (
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels
): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_FAULT_FILTER_OPTIONS.map((faultStatus) => {
    return {
      label:
        valueLabels[
          `asset:filterModalLabels.faultStatus.${faultStatus}` as keyof typeof valueLabels
        ],
      value: faultStatus,
    };
  });
const FIELD_ACTION_OPTIONS = [
  FieldActionType.ZPSR,
  FieldActionType.ZNCR,
  FieldActionType.ZPER,
  FieldActionType.ZCSP,
];
export const getFieldActionConfig = (): FilterOption<FieldActionType>[] =>
  FIELD_ACTION_OPTIONS.map((fieldType) => {
    return {
      label: (
        <TranslatedLabel
          translationKey={`asset:recalls.${ApiToFieldActionTypeMap[fieldType]}`}
          typographyVariant={'body_regular'}
        />
      ),

      value: fieldType,
    };
  });
